import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, MenuItem, Select, Tooltip, type SelectChangeEvent } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $createParagraphNode,
    $createTextNode,
    $getSelection,
    $insertNodes,
    $isElementNode,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    REDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    UNDO_COMMAND,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from '@lexical/rich-text';
import {
    $isListNode,
    INSERT_CHECK_LIST_COMMAND,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { BRAND_LUXE_PRIMARY } from '../../../pages/BrandDashboard/brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { $createImageNode } from './ImageNode';

type BlockType = 'paragraph' | HeadingTagType | 'bullet' | 'number' | 'check';
type ElementAlign = 'left' | 'center' | 'right' | 'justify';

const BLOCK_OPTIONS: { value: BlockType; label: string }[] = [
    { value: 'paragraph', label: 'Normal' },
    { value: 'h1', label: 'Heading 1' },
    { value: 'h2', label: 'Heading 2' },
    { value: 'h3', label: 'Heading 3' },
    { value: 'bullet', label: 'Bullet list' },
    { value: 'number', label: 'Numbered list' },
    { value: 'check', label: 'Checklist' },
];

function Divider() {
    return (
        <Box
            aria-hidden
            sx={{
                width: '1px',
                height: '20px',
                flex: '0 0 auto',
                alignSelf: 'center',
                bgcolor: 'rgba(255,255,255,0.14)',
                mx: '6px',
            }}
        />
    );
}

function ToolbarButton({
    title,
    active,
    disabled,
    onClick,
    children,
}: {
    title: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    onMouseDown={e => e.preventDefault()}
                    onClick={onClick}
                    sx={{
                        width: 32,
                        height: 32,
                        flex: '0 0 auto',
                        borderRadius: '6px',
                        color: active ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.72)',
                        bgcolor: active ? `${BRAND_LUXE_PRIMARY}18` : 'transparent',
                        border: active ? `1px solid ${BRAND_LUXE_PRIMARY}55` : '1px solid transparent',
                        opacity: disabled ? 0.35 : 1,
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.06)',
                            color: BRAND_LUXE_PRIMARY,
                        },
                    }}>
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

function normalizeUrl(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
        return trimmed;
    }
    return `https://${trimmed}`;
}

export function LexicalToolbar({ compact }: { compact?: boolean }) {
    const [editor] = useLexicalComposerContext();
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [blockType, setBlockType] = useState<BlockType>('paragraph');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [align, setAlign] = useState<ElementAlign>('left');
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const syncToolbar = useCallback(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsUnderline(selection.hasFormat('underline'));

        const anchor = selection.anchor.getNode();
        const element =
            anchor.getKey() === 'root'
                ? anchor
                : $findMatchingParent(anchor, $isElementNode) || anchor.getTopLevelElementOrThrow();

        const linkParent = $findMatchingParent(anchor, $isLinkNode);
        setIsLink(Boolean(linkParent));

        if ($isListNode(element)) {
            const listType = element.getListType();
            setBlockType(listType === 'number' ? 'number' : listType === 'check' ? 'check' : 'bullet');
        } else {
            const parentList = $getNearestNodeOfType(anchor, ListNode);
            if (parentList) {
                const listType = parentList.getListType();
                setBlockType(listType === 'number' ? 'number' : listType === 'check' ? 'check' : 'bullet');
            } else if ($isHeadingNode(element)) {
                setBlockType(element.getTag());
            } else {
                setBlockType('paragraph');
            }
        }

        const format = $isElementNode(element) ? element.getFormatType() : '';
        if (format === 'center' || format === 'right' || format === 'justify' || format === 'left') {
            setAlign(format);
        } else {
            setAlign('left');
        }
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }: { editorState: any }) => {
                editorState.read(() => syncToolbar());
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    syncToolbar();
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload: boolean) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload: boolean) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
        );
    }, [editor, syncToolbar]);

    const applyBlockType = useCallback(
        (next: BlockType) => {
            if (next === 'bullet') {
                editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            } else if (next === 'number') {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            } else if (next === 'check') {
                editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
            } else {
                editor.update(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection)) return;
                    if (next === 'paragraph') {
                        $setBlocksType(selection, () => $createParagraphNode());
                    } else {
                        $setBlocksType(selection, () => $createHeadingNode(next));
                    }
                });
            }
            setBlockType(next);
        },
        [editor],
    );

    const onBlockTypeChange = (event: SelectChangeEvent) => {
        applyBlockType(event.target.value as BlockType);
    };

    const toggleLink = useCallback(() => {
        if (isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            return;
        }
        const raw = window.prompt('Link URL');
        if (raw === null) return;
        const url = normalizeUrl(raw);
        if (!url) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            return;
        }
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }, [editor, isLink]);

    const insertImageFromSrc = useCallback(
        (src: string, altText = '') => {
            editor.update(() => {
                const imageNode = $createImageNode({ src, altText });
                $insertNodes([imageNode]);
                const trailing = $createParagraphNode();
                imageNode.insertAfter(trailing);
                trailing.select();
            });
        },
        [editor],
    );

    const insertMediaByUrl = useCallback(() => {
        const raw = window.prompt('Media URL (image, video file, or YouTube/Vimeo link)');
        if (!raw) return;
        const url = normalizeUrl(raw);
        if (!url) return;

        const isImage = /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(url) || url.startsWith('data:image');
        if (isImage) {
            insertImageFromSrc(url);
            return;
        }

        const label = /\.(mp4|webm|ogg)(\?|$)/i.test(url)
            ? 'Video'
            : /youtube\.com|youtu\.be|vimeo\.com/i.test(url)
            ? 'Watch media'
            : url;
        editor.update(() => {
            const link = $createLinkNode(url);
            link.append($createTextNode(label));
            const paragraph = $createParagraphNode();
            paragraph.append(link);
            $insertNodes([paragraph]);
        });
    }, [editor, insertImageFromSrc]);

    const onImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                insertImageFromSrc(reader.result, file.name);
            }
        };
        reader.readAsDataURL(file);
    };

    const insertImage = useCallback(() => {
        const choice = window.prompt('Paste an image URL, or leave blank to upload a file');
        if (choice === null) return;
        if (choice.trim()) {
            const url = normalizeUrl(choice);
            if (url) insertImageFromSrc(url);
            return;
        }
        imageInputRef.current?.click();
    }, [insertImageFromSrc]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: '2px',
                px: compact ? '8px' : '10px',
                py: compact ? '4px' : '6px',
                overflowX: 'auto',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                bgcolor: '#1a1a1a',
            }}>
            <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={onImageFileChange} />

            <Select
                size="small"
                value={blockType}
                onChange={onBlockTypeChange}
                onMouseDown={e => e.preventDefault()}
                variant="standard"
                disableUnderline
                sx={{
                    flex: '0 0 auto',
                    width: 'auto',
                    minWidth: 88,
                    maxWidth: 140,
                    color: 'rgba(255,255,255,0.85)',
                    fontSize: 13,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.55)' },
                    '& .MuiSelect-select': {
                        py: '6px',
                        pr: '24px !important',
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            bgcolor: '#1e1e1e',
                            color: '#f0f0f0',
                            border: '1px solid rgba(255,255,255,0.1)',
                        },
                    },
                }}>
                {BLOCK_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>

            <Divider />

            <ToolbarButton
                title="Undo"
                disabled={!canUndo}
                onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}>
                <UndoIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Redo"
                disabled={!canRedo}
                onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}>
                <RedoIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <Divider />

            <ToolbarButton
                title="Bold"
                active={isBold}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}>
                <FormatBoldIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Italic"
                active={isItalic}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}>
                <FormatItalicIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Underline"
                active={isUnderline}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}>
                <FormatUnderlinedIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <Divider />

            <ToolbarButton
                title="Bullet list"
                active={blockType === 'bullet'}
                onClick={() =>
                    blockType === 'bullet'
                        ? editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
                        : applyBlockType('bullet')
                }>
                <FormatListBulletedIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Numbered list"
                active={blockType === 'number'}
                onClick={() =>
                    blockType === 'number'
                        ? editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
                        : applyBlockType('number')
                }>
                <FormatListNumberedIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Checklist"
                active={blockType === 'check'}
                onClick={() =>
                    blockType === 'check'
                        ? editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
                        : applyBlockType('check')
                }>
                <ChecklistIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <Divider />

            <ToolbarButton
                title="Align left"
                active={align === 'left'}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}>
                <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Align center"
                active={align === 'center'}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}>
                <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Align right"
                active={align === 'right'}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}>
                <FormatAlignRightIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton
                title="Justify"
                active={align === 'justify'}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}>
                <FormatAlignJustifyIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>

            <Divider />

            <ToolbarButton title={isLink ? 'Remove link' : 'Insert link'} active={isLink} onClick={toggleLink}>
                {isLink ? <LinkOffIcon sx={{ fontSize: 18 }} /> : <LinkIcon sx={{ fontSize: 18 }} />}
            </ToolbarButton>
            <ToolbarButton title="Insert image" onClick={insertImage}>
                <ImageOutlinedIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
            <ToolbarButton title="Insert media URL" onClick={insertMediaByUrl}>
                <VideocamOutlinedIcon sx={{ fontSize: 18 }} />
            </ToolbarButton>
        </Box>
    );
}
